import { Star, StarBorder, MoreHoriz } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import DriveFileMove from '@mui/icons-material/DriveFileMove';
import EditIcon from '@mui/icons-material/Edit';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { useAttachmentDetails } from 'api';
import { useDeleteFileMutation } from 'api/mutations/useDeleteFileMutation';
import { useToggleFileFavouriteMutation } from 'api/mutations/useToggleFileFavouriteMutation';
import { useFileUsageQuery } from 'api/queries/useFileUsageQuery';
import { FileItem, LinkedPost } from 'api/types';
import { ReactComponent as CSVIcon } from 'assets/icons/fileType/csv.svg';
import { ReactComponent as ExcelIcon } from 'assets/icons/fileType/excel.svg';
import { ReactComponent as PPTIcon } from 'assets/icons/fileType/ppt.svg';
import { ReactComponent as TextIcon } from 'assets/icons/fileType/txt.svg';
import { ReactComponent as WordIcon } from 'assets/icons/fileType/word.svg';
import { ReactComponent as PlayIcon } from 'assets/icons/play.svg';
import { NewConfirmDialog, Spinner } from 'components';
import { CardsW } from 'features/documentCategories/components/DocumentCategories/styled';
import {
  CardW,
  IconBox,
  CardLeft,
  CardContent,
  TitleText,
  SubText,
  CardActions,
  ListTitle,
  ListMeta,
  BottomSheetHandle,
  BottomSheetHeader,
  BottomSheetHeaderText,
} from 'features/documentCategories/components/DocumentCategoryItem/styled';
import { useWindowSize } from 'hooks';
import { Links } from 'settings';
import { theme } from 'styles';
import { handleFileDownload } from 'utils';
import { formatCreatedAt } from 'utils/dateHelpers';
import { isSystemCategory } from 'validations';

import { EditFileModal } from '../EditFileModal';
import { FilePreviewModal } from '../FilePreviewModal';
import { MoveFileModal } from '../MoveFileModal';
import { VideoPreviewModal } from '../VideoPreviewModal';
import { PlayButtonContainer, StyledImage, VideoW } from './styled';

type Props = {
  item: FileItem;
  viewMode: 'grid' | 'list';
  tagId: string;
  tag: string;
};

export const FileCard = ({ item, viewMode, tagId, tag }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useWindowSize();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const menuJustClosed = useRef(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);

  const createdBy = item.created_by
    ? `${item.created_by.first_name} ${item.created_by.last_name}`
    : '';
  const createdAt = formatCreatedAt(item.created_at);

  const isImage = item.type === 'image';
  const { isLoading, data: imageData } = useAttachmentDetails({
    attachmentId: item.id,
    fileName: item.display_name,
    fileUrl: `${item.file_url.small}`,
    enabled: isImage,
  });

  const ext = item.file_extension.split('.').pop()?.toLowerCase() ?? '';
  const iconSx = { width: 20, height: 20 };
  const icon = (() => {
    if (ext === 'pdf') return <PictureAsPdfIcon sx={{ ...iconSx, color: '#e53935' }} />;
    if (ext === 'doc' || ext === 'docx') return <WordIcon />;
    if (ext === 'ppt' || ext === 'pptx') return <PPTIcon />;
    if (ext === 'xls' || ext === 'xlsx') return <ExcelIcon />;
    if (ext === 'csv') return <CSVIcon />;
    if (ext === 'txt') return <TextIcon />;
    if (item.type === 'document')
      return <InsertDriveFileOutlinedIcon sx={{ ...iconSx, color: '#217346' }} />;
    if (item.type === 'video') {
      let videoThumbnailurl = '';
      if (item?.file_url.video_uid) {
        const videoId = item?.file_url.video_uid;
        videoThumbnailurl = `https://customer-${process.env.REACT_APP_CLOUDFLARE_STREAM_ACCOUNT_ID}.cloudflarestream.com/${videoId}/thumbnails/thumbnail.gif?time=1s&height=200&duration=4s`;
      }

      return (
        <VideoW>
          {videoThumbnailurl && <StyledImage src={videoThumbnailurl} />}
          <PlayButtonContainer>
            <PlayIcon />
          </PlayButtonContainer>
        </VideoW>
      );
    }
    if (isImage && imageData) {
      if (isLoading) <Spinner />;
      return (
        <img
          src={imageData}
          alt={item.display_name}
          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
        />
      );
    }
  })();

  const { deleteFileMutation } = useDeleteFileMutation();
  const { toggleFavourite } = useToggleFileFavouriteMutation();

  const { refetch: refetchFile } = useAttachmentDetails({
    attachmentId: item.id,
    fileName: item.display_name,
    fileUrl: item.file_url.small ?? item.file_url.original,
    enabled: false,
  });

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    menuJustClosed.current = true;
    setAnchorEl(null);
    setTimeout(() => {
      menuJustClosed.current = false;
    }, 0);
  };

  const handleMenuItemClick = (action: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    handleMenuClose();
    action();
  };

  type DeleteDialogState = 'idle' | 'checking' | 'linked' | 'safe';
  const [deleteDialogState, setDeleteDialogState] = useState<DeleteDialogState>('idle');
  const [linkedPosts, setLinkedPosts] = useState<LinkedPost[]>([]);

  const { refetch: checkUsage } = useFileUsageQuery({ fileId: item.id, enabled: false });

  const handleDeleteTarget = async () => {
    setDeleteDialogState('idle');
    await deleteFileMutation({ id: item.id });
  };

  const handleDeleteClick = async () => {
    setDeleteDialogState('checking');
    try {
      const result = await checkUsage();
      if (result.data?.is_linked) {
        setLinkedPosts(result.data.linked_posts);
        setDeleteDialogState('linked');
      } else {
        setDeleteDialogState('safe');
      }
    } catch {
      setDeleteDialogState('safe');
    }
  };

  const handleDownload = async () => {
    const { data } = await refetchFile();
    if (data?.data) {
      await handleFileDownload({ blobFile: data.data, fileName: item.display_name });
    }
  };

  const onClickFavourite = () => {
    toggleFavourite({ id: item.id, isFavourite: !!item.is_favourite });
  };

  const modals = (
    <div
      role="presentation"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Dialog
        open={deleteDialogState === 'checking' || deleteDialogState === 'linked'}
        onClose={() => setDeleteDialogState('idle')}
        PaperProps={{ sx: { borderRadius: '12px', padding: '8px', maxWidth: 600, width: '100%' } }}
      >
        {deleteDialogState === 'checking' && (
          <DialogContent
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}
          >
            <CircularProgress />
          </DialogContent>
        )}

        {deleteDialogState === 'linked' && (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>
              <Typography
                component="span"
                sx={{
                  fontWeight: 700,
                  fontSize: 'inherit',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-all',
                }}
              >
                {t('document.list.cannotDeleteFile', { name: item.display_name })}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('document.list.fileAttachedToPosts', { count: linkedPosts.length })}
              </Typography>
              {linkedPosts.length > 0 && (
                <List dense disablePadding>
                  {linkedPosts.slice(0, 2).map((post) => {
                    const places: string[] = [];
                    if (post.newsfeed) places.push(t('post.create.form.placeGroups.newsfeed'));
                    post.categories?.forEach((c) => places.push(c));
                    post.profiles?.forEach((p) => places.push(p));
                    const placesText = places.length > 0 ? ` — used in: ${places.join(', ')}` : '';
                    return (
                      <ListItem
                        key={post.id}
                        disableGutters
                        sx={{ py: 0, cursor: 'pointer', '&:hover': { opacity: 0.7 } }}
                        onClick={() => {
                          setDeleteDialogState('idle');
                          navigate(generatePath(Links.we.posts.index, { postId: post.id }));
                        }}
                      >
                        <Typography variant="body2" color="primary">
                          &bull; &ldquo;{post.title}&rdquo;{placesText}
                        </Typography>
                      </ListItem>
                    );
                  })}
                  {linkedPosts.length > 2 && (
                    <ListItem disableGutters sx={{ py: 0 }}>
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        +{linkedPosts.length - 2} more post{linkedPosts.length - 2 !== 1 ? 's' : ''}
                      </Typography>
                    </ListItem>
                  )}
                </List>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                variant="contained"
                onClick={() => setDeleteDialogState('idle')}
                style={{
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '14px',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  color: theme.palette.secondary.dark,
                  border: `1px solid ${theme.palette.primary.grey}`,
                  backgroundColor: theme.palette.white.main,
                }}
              >
                {t('resources.documentCategories.files.removeModal.buttons.cancel')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <NewConfirmDialog
        title={t('resources.documentCategories.files.removeModal.title', {
          folderTitle: item.display_name,
        })}
        subTitle={
          item.locations.length > 1
            ? t('resources.documentCategories.files.removeModal.subtitle2')
            : t('resources.documentCategories.files.removeModal.subtitle')
        }
        open={deleteDialogState === 'safe'}
        onConfirm={handleDeleteTarget}
        onClose={() => setDeleteDialogState('idle')}
      />
      {isEditModalOpen && (
        <EditFileModal
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          fileId={item.id}
          currentName={item.display_name}
          currentLocations={item.locations}
        />
      )}
      {isMoveModalOpen && (
        <MoveFileModal
          open={isMoveModalOpen}
          onClose={() => setIsMoveModalOpen(false)}
          item={item}
          categoryId={tagId}
          tag={tag}
          currentFolderId={item.locations[0]?.folder_id}
        />
      )}
    </div>
  );

  const menuContent = [
    !isSystemCategory(tag) && (
      <MenuItem
        key="move"
        onClick={handleMenuItemClick(() => setIsMoveModalOpen(true))}
        sx={{ '&:hover': { backgroundColor: theme.palette.info.light } }}
      >
        <ListItemIcon>
          <DriveFileMove fontSize="small" />
        </ListItemIcon>
        <ListItemText>{t('resources.documentCategories.files.menu.move')}</ListItemText>
      </MenuItem>
    ),
    <MenuItem
      key="download"
      onClick={handleMenuItemClick(handleDownload)}
      sx={{ '&:hover': { backgroundColor: theme.palette.info.light } }}
    >
      <ListItemIcon>
        <DownloadIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>{t('resources.documentCategories.files.menu.download')}</ListItemText>
    </MenuItem>,
    <MenuItem
      key="edit"
      onClick={handleMenuItemClick(() => setIsEditModalOpen(true))}
      sx={{ '&:hover': { backgroundColor: theme.palette.info.light } }}
    >
      <ListItemIcon>
        <EditIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>{t('resources.documentCategories.files.menu.edit')}</ListItemText>
    </MenuItem>,
    <MenuItem
      key="delete"
      onClick={handleMenuItemClick(handleDeleteClick)}
      sx={{ '&:hover': { backgroundColor: theme.palette.info.light } }}
    >
      <ListItemIcon>
        <DeleteIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>{t('resources.documentCategories.files.menu.delete')}</ListItemText>
    </MenuItem>,
  ];

  const menuItems = isMobile ? (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleMenuClose}
      PaperProps={{
        sx: {
          borderRadius: '20px 20px 0 0',
          pb: '16px',
        },
      }}
    >
      <BottomSheetHandle />
      <BottomSheetHeader>
        <IconBox>{icon}</IconBox>
        <BottomSheetHeaderText>
          <span>{item.display_name}</span>
          <span>{item.type}</span>
        </BottomSheetHeaderText>
      </BottomSheetHeader>
      {menuContent}
    </Drawer>
  ) : (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleMenuClose}
      PaperProps={{
        sx: {
          width: '250px',
          padding: '8px',
          borderRadius: '8px',
          border: '1px solid #ddd',
        },
      }}
      MenuListProps={{ disablePadding: true }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      {menuContent}
    </Menu>
  );

  if (viewMode === 'list') {
    return (
      <>
        <CardW
          viewMode="list"
          onClick={() => {
            if (!menuJustClosed.current) setIsPreviewOpen(true);
          }}
          isManageable
        >
          <IconBox>{icon}</IconBox>
          <ListTitle>{item.display_name}</ListTitle>
          <ListMeta>{item.type}</ListMeta>
          <ListMeta>{createdBy}</ListMeta>
          <ListMeta>{createdAt}</ListMeta>
          <CardActions>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClickFavourite();
              }}
            >
              {item.is_favourite ? (
                <Star fontSize="small" htmlColor="#f59e0b" />
              ) : (
                <StarBorder fontSize="small" />
              )}
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleClick(e);
              }}
            >
              <MoreHoriz fontSize="small" />
            </IconButton>
            {menuItems}
          </CardActions>
          {modals}
        </CardW>
        {isPreviewOpen && (
          <FilePreviewModal
            file={item}
            open={isPreviewOpen}
            onClose={() => setIsPreviewOpen(false)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <CardW
        onClick={() => {
          if (!menuJustClosed.current) setIsPreviewOpen(true);
        }}
        isManageable
      >
        <CardLeft>
          <IconBox>{icon}</IconBox>
          <CardContent>
            <TitleText>{item.display_name}</TitleText>
            <SubText>{item.type}</SubText>
            <SubText>{createdBy}</SubText>
            <SubText>{createdAt}</SubText>
          </CardContent>
        </CardLeft>
        <CardActions>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onClickFavourite();
            }}
          >
            {item.is_favourite ? (
              <Star fontSize="small" htmlColor="#f59e0b" />
            ) : (
              <StarBorder fontSize="small" />
            )}
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleClick(e);
            }}
          >
            <MoreHoriz fontSize="small" />
          </IconButton>
          {menuItems}
        </CardActions>
        {modals}
      </CardW>
      {isPreviewOpen && item.type === 'video' ? (
        <VideoPreviewModal
          file={item}
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
        />
      ) : (
        <FilePreviewModal
          file={item}
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
        />
      )}
    </>
  );
};

type ListProps = {
  files: FileItem[];
  viewMode: 'grid' | 'list';
  tagId: string;
  tag: string;
};

export const FileCardList = ({ files, viewMode, tagId, tag }: ListProps) => {
  const { t } = useTranslation();

  if (!files.length) return null;

  return (
    <>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
        {t('resources.documentCategories.files.title')}
      </div>
      <CardsW viewMode={viewMode}>
        {[...files]
          .sort((a, b) => (b.is_favourite ? 1 : 0) - (a.is_favourite ? 1 : 0))
          .map((file) => (
            <FileCard key={file.id} item={file} viewMode={viewMode} tagId={tagId} tag={tag} />
          ))}
      </CardsW>
    </>
  );
};
